import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Alert,
  CircularProgress,
  TextField,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  OutlinedInput,
  IconButton,
  Popover,
  FormControlLabel,
  FormControl,
  Tooltip,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete
} from '@mui/material';



import {
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  ArrowBack as ArrowBackIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Visibility as VisibilityIcon,
  ShowChart as ShowChartIcon,
  Close as CloseIcon,
  Restore as RestoreIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { ProductMeasurement } from '../types';
import { measurementsApi, lambdaGroupsApi, productsApi, tableSettingsApi } from '../services/api';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import logoImage from '../images/Logo thermowool.png';
import dayjs, { Dayjs } from 'dayjs';
import { UserRoleContext } from './Layout';

import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';


// Definicija svih mogućih kolona
const ALL_COLUMNS = [
  { key: 'productType', label: 'Tip proizvoda' },
  { key: 'lambdaGroup', label: 'Lambda Grupa' },
  { key: 'datumProizvodnje', label: 'Datum proizvodnje' },
  { key: 'datumTestiranja', label: 'Datum testiranja' },
  { key: 'smena', label: 'Smena' },
  { key: 'measuredBy', label: 'Merio' },
  { key: 'controlledBy', label: 'Kontrolisao' },
  { key: 'status', label: 'Status' },
  { key: 'secena', label: 'Secena' },
  { key: 'gustina', label: 'Gustina' },
  { key: 'sm', label: 'SM%' },
  { key: 'duzina', label: 'Duzina (SV)' },
  { key: 'sirina', label: 'Sirina (SV)' },
  { key: 'vlakana', label: 'Distribucija vlakana (SV)' },
  { key: 'debljina', label: 'Debljina (SV)' },
  { key: 'afr', label: 'AFr (SV)' },
  { key: 'lambda', label: 'Lambda vrednost' },
  { key: 'lambdaGustinga', label: 'λ Gustinga' },
  { key: 'lambda9090', label: 'Lambda 90/90' },
  { key: 'cs10', label: 'CS (10)' },
  { key: 'napomena', label: 'Napomena' }
];

// Kolone koje mogu da se prikažu na grafu (kriva linija kroz vreme)
const GRAPHABLE_COLUMN_KEYS = ['gustina', 'sm', 'duzina', 'sirina', 'vlakana', 'debljina', 'afr', 'lambda', 'lambdaGustinga'] as const;
const GRAPHABLE_FIELD_MAP: Record<string, { field: string; label: string }> = {
  gustina: { field: 'gustina', label: 'Gustina' },
  sm: { field: 'procenatSM', label: 'SM%' },
  duzina: { field: 'avgDuzina', label: 'Duzina (SV)' },
  sirina: { field: 'avgSirina', label: 'Sirina (SV)' },
  vlakana: { field: 'avgDistribucija', label: 'Distribucija vlakana (SV)' },
  debljina: { field: 'avgThickness', label: 'Debljina (SV)' },
  afr: { field: 'avgAFr', label: 'AFr (SV)' },
  lambda: { field: 'lambdaVrednost', label: 'Lambda vrednost' },
  lambdaGustinga: { field: 'lambdaGustinga', label: 'λ Gustinga' },
};

const CHART_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B6B', '#4ECDC4'];

const FinalReport: React.FC = () => {
  const navigate = useNavigate();
  const { role } = useContext(UserRoleContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [measurements, setMeasurements] = useState<ProductMeasurement[]>([]);


  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [lambdaGroups, setLambdaGroups] = useState<{ _id: string; name: string }[]>([]);
  const [selectedLambdaGroup, setSelectedLambdaGroup] = useState<string>('');
  const [products, setProducts] = useState<any[]>([]);
  const [selectedThickness, setSelectedThickness] = useState<string>('');
  const [thicknessOptions, setThicknessOptions] = useState<string[]>([]);
  const [selectedLambdaGroups, setSelectedLambdaGroups] = useState<string[]>([]);
  const [selectedThicknesses, setSelectedThicknesses] = useState<string[]>([]);
  const [selectedProductsFilter, setSelectedProductsFilter] = useState<string[]>([]);
  const [productOptionsForFilter, setProductOptionsForFilter] = useState<string[]>([]);

  // State za kolone
  const [columns, setColumns] = useState(ALL_COLUMNS.map((col: any, i: number) => ({ ...col, visible: true, order: i })));
  const [columnsLoading, setColumnsLoading] = useState(false);

  // State za popover
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openPopover = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const closePopover = () => setAnchorEl(null);

  // State za dijalog sa napomenom
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteDialogText, setNoteDialogText] = useState<string>('');

  const handleOpenNoteDialog = (text: string) => {
    setNoteDialogText(text);
    setNoteDialogOpen(true);
  };

  const handleCloseNoteDialog = () => {
    setNoteDialogOpen(false);
    setNoteDialogText('');
  };

  // Export Graph – dijalog sa linijskim grafom
  const [graphDialogOpen, setGraphDialogOpen] = useState(false);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadData = async () => {
      await loadMeasurements();
    };
    loadData();
  }, []);

  // Učitaj lambda grupe i sve proizvode
  useEffect(() => {
    lambdaGroupsApi.getAll().then(setLambdaGroups);
    productsApi.getAll().then(setProducts);
  }, []);

  // Filtriraj proizvode za izabrane lambda grupe
  useEffect(() => {
    if (selectedLambdaGroups.length > 0) {
      const filtered = products.filter((p: any) => selectedLambdaGroups.includes(p.lambdaGroup));
      const thicknesses = Array.from(new Set(filtered.map((p: any) => String(p.thickness)))).map(String);
      setThicknessOptions(thicknesses);
    } else {
      setThicknessOptions([]);
    }
    setSelectedThicknesses([]);
    // Opcije proizvoda za filter (po nazivu) na osnovu izabranih lambda grupa
    if (selectedLambdaGroups.length > 0) {
      const byGroup = products.filter((p: any) => selectedLambdaGroups.includes(p.lambdaGroup));
      const names = Array.from(new Set(byGroup.map((p: any) => p.name)));
      setProductOptionsForFilter(names);
    } else {
      setProductOptionsForFilter([]);
    }
    setSelectedProductsFilter([]);
  }, [selectedLambdaGroups, products]);

  // Učitaj podešavanja kolona sa backend-a
  useEffect(() => {
    const fetchSettings = async () => {
      setColumnsLoading(true);
      try {
        const settings = await tableSettingsApi.getColumnSettings();
        if (settings && settings.columns) {
          // Merge ALL_COLUMNS with backend settings to ensure all columns are present
          const mapped = ALL_COLUMNS.map((def, i) => {
            const found = settings.columns.find((col: any) => col.key === def.key);
            return found ? { ...def, visible: found.visible, order: found.order } : { ...def, visible: true, order: i };
          });
          // Sort by order to maintain saved column order
          const sorted = mapped.sort((a, b) => a.order - b.order);
          setColumns(sorted);
        } else {
          setColumns(ALL_COLUMNS.map((col, i) => ({ ...col, visible: true, order: i })));
        }
      } catch (e) {
        setColumns(ALL_COLUMNS.map((col, i) => ({ ...col, visible: true, order: i })));
      }
      setColumnsLoading(false);
    };
    fetchSettings();
  }, []);





  // Handler za promenu vidljivosti jedne kolone
  const handleToggleColumnVisibility = (key: string) => {
    setColumns((cols: any[]) => {
      const next = cols.map((col: any) => col.key === key ? { ...col, visible: !col.visible } : col);
      const payload = next.map((col: any, i: number) => ({ key: col.key, visible: col.visible, order: i }));
      tableSettingsApi.saveColumnSettings(payload, false).catch(() => { /* čuvanje u pozadini, bez alerta */ });
      return next;
    });
  };

  // Sačuvaj podešavanja na backend (koristi trenutni state kolona)
  const saveColumns = async (isGlobal = false) => {
    await tableSettingsApi.saveColumnSettings(columns.map((col: any, i: number) => ({ key: col.key, visible: col.visible, order: i })), isGlobal);
  };

  // Reset na globalna podešavanja
  const resetToGlobalSettings = async () => {
    setColumnsLoading(true);
    try {
      const settings = await tableSettingsApi.getColumnSettings();
      if (settings && settings.columns) {
        const mapped = ALL_COLUMNS.map((def, i) => {
          const found = settings.columns.find((col: any) => col.key === def.key);
          return found ? { ...def, visible: found.visible, order: found.order } : { ...def, visible: true, order: i };
        });
        // Sort by order to maintain saved column order
        const sorted = mapped.sort((a, b) => a.order - b.order);
        setColumns(sorted);
      }
    } catch (e) {
      // console.error('Greška pri resetovanju na globalna podešavanja:', e);
    }
    setColumnsLoading(false);
  };


  const loadMeasurements = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await measurementsApi.getAll();
      setMeasurements(data);
    } catch (err) {
      setError('Greška pri učitavanju podataka');
    } finally {
      setLoading(false);
    }
  };



  // Funkcija za prikaz vrednosti po koloni (koristi se i za tabelu i za eksport)
  function getColumnValue(
    col: { key: string; label: string },
    m: any,
    products: any[],
    lambdaGroups: any[]
  ): string {
    switch (col.key) {
      case 'productType': return m.productType;
      case 'lambdaGroup': {
        const product = products.find((p) => p.name === m.productType);
        const group = lambdaGroups.find((g) => g.name === (product ? product.lambdaGroup : ''));
        if (!group) return '';
        const cleanName = cleanLambdaGroupName(group.name);
        const match = cleanName.match(/^\s*grupa\s*(\d+)/i);
        if (match) return match[1];
        return cleanName;
      }
      case 'datumProizvodnje': return m.datumProizvodnje ? format(new Date(m.datumProizvodnje), 'dd.MM.yyyy') : '';
      case 'datumTestiranja': return m.datumTestiranja ? format(new Date(m.datumTestiranja), 'dd.MM.yyyy') : '';
      case 'smena': return m.smena;
      case 'measuredBy': return m.kontrolisao;
      case 'controlledBy': return m.verified ? m.verifiedBy : m.notVerified ? m.notVerifiedBy : '';
      case 'status': return m.verified ? 'Verifikovano' : m.notVerified ? 'Odbijeno' : 'Ceka pregled';
      case 'secena': {
        const secenaVal = m.secena && m.secena.toLowerCase() !== 'ne' && m.secena !== 'bez_secena';
        if (!secenaVal) return 'Ne';
        if (m.pozicijaSecenja && String(m.pozicijaSecenja).trim() !== '') {
          return `Da (${m.pozicijaSecenja})`;
        }
        return 'Da';
      }
      case 'gustina': return m.gustina !== undefined && m.gustina !== null && !isNaN(Number(m.gustina)) ? Number(m.gustina).toFixed(2) : '';
      case 'sm': return m.procenatSM !== undefined && m.procenatSM !== null && !isNaN(Number(m.procenatSM)) ? Number(m.procenatSM).toFixed(2) : '';
      case 'duzina': return m.avgDuzina !== undefined && m.avgDuzina !== null && !isNaN(Number(m.avgDuzina)) ? Number(m.avgDuzina).toFixed(2) : '';
      case 'sirina': return m.avgSirina !== undefined && m.avgSirina !== null && !isNaN(Number(m.avgSirina)) ? Number(m.avgSirina).toFixed(2) : '';
      case 'vlakana': return m.avgDistribucija !== undefined && m.avgDistribucija !== null && !isNaN(Number(m.avgDistribucija)) ? Number(m.avgDistribucija).toFixed(2) : '';
      case 'debljina': return m.avgThickness !== undefined && m.avgThickness !== null && !isNaN(Number(m.avgThickness)) ? Number(m.avgThickness).toFixed(2) : '';
      case 'afr': return m.avgAFr !== undefined && m.avgAFr !== null && !isNaN(Number(m.avgAFr)) ? Number(m.avgAFr).toFixed(2) : '';
      case 'lambda': return 'lambdaVrednost' in m && m.lambdaVrednost !== undefined && m.lambdaVrednost !== null && !isNaN(Number(m.lambdaVrednost)) ? Number(m.lambdaVrednost).toFixed(2) : '';
      case 'lambdaGustinga': return 'lambdaGustinga' in m && m.lambdaGustinga !== undefined && m.lambdaGustinga !== null && !isNaN(Number(m.lambdaGustinga)) ? Number(m.lambdaGustinga).toFixed(2) : '';
      case 'lambda9090': return m.lambda9090 !== undefined && m.lambda9090 !== null && !isNaN(Number(m.lambda9090)) ? Number(m.lambda9090).toFixed(2) : '';
      case 'cs10': return 'cs10' in m && m.cs10 !== undefined && m.cs10 !== null && !isNaN(Number(m.cs10)) ? Number(m.cs10).toFixed(2) : '';
      case 'napomena': return m.napomena || '';
      default: return '';
    }
  }

  // Dodaj helper funkciju za transliteraciju latinice u ASCII
  function latinToAscii(str: string): string {
    return str
      .replace(/č/g, 'c')
      .replace(/ć/g, 'c')
      .replace(/ž/g, 'z')
      .replace(/š/g, 's')
      .replace(/đ/g, 'dj')
      .replace(/Č/g, 'C')
      .replace(/Ć/g, 'C')
      .replace(/Ž/g, 'Z')
      .replace(/Š/g, 'S')
      .replace(/Đ/g, 'Dj');
  }

  // Funkcija za transliteraciju lambda grupe (uklanja specijalne znakove, npr. strelice i λ)
  function cleanLambdaGroupName(name: string): string {
    // Uklanja sve što nije slovo, broj, razmak ili osnovni interpunkcijski znak, uključujući λ
    return name.replace(/[λ»«→←⇨⇦›‹\-]/g, '').replace(/\s{2,}/g, ' ').trim();
  }

  // Excel eksport
  const exportToExcel = () => {
    const visibleColumns = columns.filter((c: any) => c.visible).sort((a: any, b: any) => a.order - b.order);
    // Pripremi podatke za eksport
          const data = [visibleColumns.map((col: any) => col.label)];
      sortedMeasurements.forEach((m: any) => {
      const row: string[] = [];
      visibleColumns.forEach((col: { key: string; label: string }) => {
        row.push(getColumnValue(col, m, products, lambdaGroups));
      });
      data.push(row);
    });
    // Kreiraj worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    // Stilizuj header: bold, siva pozadina, obrubi
    if (worksheet['!ref']) {
      const headerRange = XLSX.utils.decode_range(worksheet['!ref']);
      for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
        const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: C })];
        if (cell) {
          cell.s = {
            font: { bold: true },
            fill: { fgColor: { rgb: 'E0E0E0' } },
            border: {
              top: { style: 'thin', color: { rgb: 'AAAAAA' } },
              bottom: { style: 'thin', color: { rgb: 'AAAAAA' } },
              left: { style: 'thin', color: { rgb: 'AAAAAA' } },
              right: { style: 'thin', color: { rgb: 'AAAAAA' } },
            },
            alignment: { horizontal: 'center', vertical: 'center' },
          };
        }
      }
    }
    // Automatska širina kolona
    worksheet['!cols'] = visibleColumns.map((col: any, idx: number) => {
      const maxLen = Math.max(
        col.label.length,
        ...data.slice(1).map(row => (row[idx] ? String(row[idx]).length : 0))
      );
      return { wch: Math.min(Math.max(maxLen + 2, 10), 30) };
    });
    // Kreiraj workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Tehnička merenja');
    // Sačuvaj fajl
    XLSX.writeFile(workbook, `tehnicka-merenja-${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
  };

  const exportToPDF = () => {
    try {
      // console.log('Započinjem PDF eksport...');
      
      // Kreiraj PDF u landscape orijentaciji
      const doc = new jsPDF('landscape');
      
      // Proveri da li je autoTable funkcija dostupna
      if (typeof autoTable !== 'function') {
        throw new Error('autoTable funkcija nije dostupna. Proverite da li je jspdf-autotable pravilno instaliran.');
      }
      
      // Dodaj moderno zaglavlje sa gradijentom
      doc.setFillColor(41, 128, 185); // Plava pozadina
      doc.rect(0, 0, doc.internal.pageSize.width, 45, 'F');
      
      // Dodaj logotip
      try {
        doc.addImage(logoImage, 'PNG', 15, 8, 45, 18);
      } catch (e) {
        // console.warn('Logotip nije mogao da se doda:', e);
      }
      
      // Dodaj naslov u zaglavlju
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Izveštaj – Završna tabela', 70, 22);
      
      // Dodaj podnaslov
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Tehnicka merenja proizvoda', 70, 32);
      
      // Pripremi podatke za informacije
      const userFromStorage = JSON.parse(localStorage.getItem('user') || '{}');
      let dateRangeText = '';
      if (startDate && endDate) {
        dateRangeText = `${startDate.format('DD.MM.YYYY')} - ${endDate.format('DD.MM.YYYY')}`;
      } else if (startDate) {
        dateRangeText = `${startDate.format('DD.MM.YYYY')}`;
      } else if (endDate) {
        dateRangeText = `${endDate.format('DD.MM.YYYY')}`;
      } else {
        dateRangeText = format(new Date(), 'dd.MM.yyyy');
      }
      
      // Dodaj informacije o izveštaju
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Period: ${dateRangeText}`, 15, 55);
      doc.text(`Ukupno merenja: ${sortedMeasurements.length}`, 15, 60);
      doc.text(`Izveštaj predao: ${userFromStorage.username || ''}`, 15, 65);
      doc.text(`Datum izveštaja: ${format(new Date(), 'dd.MM.yyyy HH:mm')}`, 15, 70);
      
      // PDF eksport - tabela
      const visibleColumns = columns.filter(c => c.visible).sort((a, b) => a.order - b.order);
      const head = [visibleColumns.map(col => col.label)];

      // Pripremi telo tabele i konvertuj srpska latinicna slova u ASCII za napomenu
      const body = sortedMeasurements.map(m =>
        visibleColumns.map(col => {
          let value = getColumnValue(col, m, products, lambdaGroups);
          // Normalizuj line break-ove na \n da bi autoTable lepo prelamao redove
          if (typeof value === 'string') {
            value = value.replace(/\r\n/g, '\n');
          }
          if (col.key === 'napomena' && value) {
            value = latinToAscii(value);
          }
          return value;
        })
      );
      
      // Optimizuj širine kolona za bolji prikaz
      const columnStyles = visibleColumns.reduce((acc, col, idx) => {
        let cellWidth = 'auto';
        let fontSize = 8;
        let halign = 'left';
        
        switch (col.key) {
          case 'productType':
            cellWidth = '35';
            break;
          case 'lambdaGroup':
            cellWidth = '25';
            halign = 'center';
            break;
          case 'datumProizvodnje':
          case 'datumTestiranja':
            cellWidth = '25';
            halign = 'center';
            break;
          case 'smena':
            cellWidth = '20';
            halign = 'center';
            break;
          case 'measuredBy':
          case 'controlledBy':
            cellWidth = '30';
            break;
          case 'status':
            cellWidth = '25';
            halign = 'center';
            break;
          case 'secena':
            cellWidth = '20';
            break;
          case 'napomena':
            cellWidth = '50';
            fontSize = 7;
            halign = 'left';
            break;
          case 'gustina':
          case 'sm':
          case 'duzina':
          case 'sirina':
          case 'vlakana':
          case 'debljina':
          case 'afr':
          case 'lambda':
          case 'lambdaGustinga':
          case 'lambda9090':
          case 'cs10':
            cellWidth = '22';
            fontSize = 7;
            halign = 'center';
            break;
          default:
            cellWidth = 'auto';
        }
        
        acc[idx] = { 
          cellWidth, 
          fontSize,
          halign,
          overflow: 'linebreak',
          cellPadding: 2
        };
        return acc;
      }, {} as Record<number, any>);
      
      autoTable(doc, {
        startY: 80,
        head,
        body,
        theme: 'grid',
        headStyles: {
          fillColor: [52, 73, 94], // Tamno plava
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 7,
          halign: 'center',
          cellPadding: 3
        },
        styles: {
          fontSize: 6,
          cellPadding: 2,
          overflow: 'linebreak',
          lineColor: [200, 200, 200],
          lineWidth: 0.1
        },
        alternateRowStyles: {
          fillColor: [248, 249, 250]
        },
        tableWidth: 'auto',
        columnStyles,
        margin: { top: 5, right: 15, bottom: 15, left: 15 }
      });
      
      // Dodaj footer sa brojem strana
      const pageCount = doc.getNumberOfPages();
      const pageWidth = doc.internal.pageSize.width;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(6);
        doc.setTextColor(100, 100, 100);
        
        // Broj stranice u levom uglu
        doc.text(`Strana ${i} od ${pageCount}`, 15, 200);
        
        // Centriraj glavni tekst u footer-u
        const footerText = `Thermowool - Tehnicka merenja`;
        const textWidth = doc.getTextWidth(footerText);
        const xPosition = (pageWidth - textWidth) / 2; // Centriraj tekst
        doc.text(footerText, xPosition, 200);
        
        // Datum u desnom uglu
        const dateText = format(new Date(), 'dd.MM.yyyy');
        const dateWidth = doc.getTextWidth(dateText);
        doc.text(dateText, pageWidth - dateWidth - 15, 200);
      }
      
      doc.save(`tehnicka-merenja-${format(new Date(), 'dd-MM-yyyy-HHmm')}.pdf`);
      // console.log('PDF uspešno sačuvan');
    } catch (error) {
      // console.error('Greška pri eksportu PDF-a:', error);
      const errorMessage = error instanceof Error ? error.message : 'Nepoznata greška';
      // console.error('Detalji greške:', errorMessage);
      alert(`Greška pri eksportu PDF-a: ${errorMessage}`);
    }
  };

  // Filtriraj merenja po filterima
  const [excludeRejected, setExcludeRejected] = useState(false);
  const [excludeVerified, setExcludeVerified] = useState(false);
  
  // State za sortiranje
  const [sortBy, setSortBy] = useState<'datumProizvodnje' | 'datumTestiranja'>('datumProizvodnje');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // State za paginaciju
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const filteredMeasurements = measurements.filter(m => {
    // Datum filtriranje
    if (startDate && new Date(m.datumProizvodnje) < startDate.toDate()) return false;
    if (endDate && new Date(m.datumProizvodnje) > endDate.toDate()) return false;
    
    // Lambda grupe
    if (selectedLambdaGroups.length > 0) {
      const product = products.find((p) => p.name === m.productType);
      if (!product || !selectedLambdaGroups.includes(product.lambdaGroup)) return false;
    }
    // Debljine
    if (selectedThicknesses.length > 0) {
      const product = products.find((p) => p.name === m.productType);
      if (!product || !selectedThicknesses.includes(String(product.thickness))) return false;
    }
    // Proizvodi (filter po nazivu)
    if (selectedProductsFilter.length > 0 && !selectedProductsFilter.includes(m.productType)) return false;
    // Odbijeni
    if (excludeRejected && m.notVerified) return false;
    if (excludeVerified && m.verified) return false;
    return true;
  });

  // Sortiranje merenja
  const sortedMeasurements = [...filteredMeasurements].sort((a, b) => {
    if (sortBy === 'datumProizvodnje') {
      const aDate = new Date(a.datumProizvodnje).getTime();
      const bDate = new Date(b.datumProizvodnje).getTime();
      return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
    } else if (sortBy === 'datumTestiranja') {
      const aDate = new Date(a.datumTestiranja).getTime();
      const bDate = new Date(b.datumTestiranja).getTime();
      return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
    }
    return 0;
  });

  // Kolone koje se mogu prikazati na grafu (vidljive + u listi grafabilnih)
  const visibleGraphableColumns = useMemo(() => {
    const visible = columns.filter((c: any) => c.visible && GRAPHABLE_COLUMN_KEYS.includes(c.key));
    return visible.sort((a: any, b: any) => a.order - b.order);
  }, [columns]);
  const hasGraphableColumns = visibleGraphableColumns.length > 0;

  // Podaci za Export Graph: jedan tačaka po merenju (datum + vrednosti uključenih kolona)
  const graphChartData = useMemo(() => {
    return sortedMeasurements.map((m: any) => {
      const point: Record<string, string | number | null> = {
        date: format(new Date(m.datumProizvodnje), 'dd.MM.yyyy'),
        dateSort: new Date(m.datumProizvodnje).getTime(),
        productType: m.productType,
      };
      visibleGraphableColumns.forEach((col: any) => {
        const config = GRAPHABLE_FIELD_MAP[col.key];
        if (!config) return;
        const raw = (m as any)[config.field];
        const num = raw !== undefined && raw !== null && !isNaN(Number(raw)) ? Number(raw) : null;
        point[col.key] = num;
      });
      return point;
    });
  }, [sortedMeasurements, visibleGraphableColumns]);

  // Paginacija
  const paginatedMeasurements = sortedMeasurements.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  
  // Handlers za paginaciju
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Resetuj stranicu kada se menjaju filteri ili sortiranje
  useEffect(() => {
    setPage(0);
  }, [selectedLambdaGroups, selectedThicknesses, excludeRejected, excludeVerified, startDate, endDate, sortBy, sortOrder]);

  const handleResetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedLambdaGroups([]);
    setSelectedThicknesses([]);
    setSelectedProductsFilter([]);
    setExcludeRejected(false);
    setExcludeVerified(false);
    setSortBy('datumProizvodnje');
    setSortOrder('desc');
    setPage(0);
  };

  const hasActiveFilters =
    startDate != null ||
    endDate != null ||
    selectedLambdaGroups.length > 0 ||
    selectedThicknesses.length > 0 ||
    selectedProductsFilter.length > 0 ||
    excludeRejected ||
    excludeVerified;

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ 
        py: isMobile ? 2 : 4,
        '@media (max-width: 480px)': {
          py: 1,
        }
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: isMobile ? 2 : 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (role === 'tehnolog') {
    return (
      <Container maxWidth="md" sx={{ 
        py: isMobile ? 2 : 4,
        '@media (max-width: 480px)': {
          py: 1,
        }
      }}>
        <Alert severity="error">Samo admin, master admin ili direktor može da pristupi ovoj stranici.</Alert>
      </Container>
    );
  }

  const COLUMN_SELECT_MENU_PROPS = {
    PaperProps: {
      style: {
        maxHeight: 400,
        width: 450,
      },
    },
  };

  interface SortableColumnItemProps {
    col: any;
    idx: number;
    id: string;
    onToggleVisibility: (key: string) => void;
    children?: React.ReactNode;
  }

  const SortableColumnItem: React.FC<SortableColumnItemProps> = ({ col, idx, id, onToggleVisibility, children }) : React.ReactElement => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };
    return (
      <div ref={setNodeRef} style={style}>
        <Box
          {...attributes}
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 1,
            p: 1,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: isDragging ? 'grey.200' : 'background.paper',
            opacity: isDragging ? 0.7 : 1,
            cursor: 'default', // važno: ne grab na celom redu
          }}
        >
          <Checkbox
            checked={col.visible}
            onChange={() => onToggleVisibility(col.key)}
            sx={{ pointerEvents: 'auto' }}
          />
          <ListItemText primary={col.label} sx={{ flex: 1, minWidth: 0 }} />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {/* Drag handle je samo ikonica */}
            <span {...listeners} style={{ fontSize: 18, cursor: 'grab', color: '#888' }}>☰</span>
          </Box>
          {children}
        </Box>
      </div>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ 
      py: isMobile ? 2 : 4,
      '@media (max-width: 480px)': {
        py: 1,
        px: 0.5,
        maxWidth: '100vw',
        overflow: 'hidden',
        width: '100%',
      }
    }}>
      <Box sx={{ mb: isMobile ? 2 : 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{ 
            mb: isMobile ? 1 : 2,
            '@media (max-width: 480px)': {
              fontSize: '0.875rem',
              minHeight: 36,
            }
          }}
        >
          Nazad
        </Button>
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom
          sx={{
            '@media (max-width: 480px)': {
              fontSize: '1.5rem',
              mb: 1,
            }
          }}
        >
          Završna tabela
        </Typography>
        <Typography 
          variant="subtitle1" 
          color="text.secondary"
          sx={{
            '@media (max-width: 480px)': {
              fontSize: '0.875rem',
            }
          }}
        >
          Pregled svih tehničkih merenja proizvoda
        </Typography>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {/* UI za filtere (dodaj iznad tabele/dugmeta za eksport) */}
              <Box sx={{ 
          display: 'flex', 
          gap: isMobile ? 1 : 2, 
          mb: isMobile ? 2 : 3, 
          flexWrap: 'wrap',
          flexDirection: isMobile ? 'column' : 'row',
          width: '100%',
          overflow: 'hidden',
          '@media (max-width: 480px)': {
            gap: 0.5,
            mb: 1.5,
            maxWidth: '100vw',
          }
        }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Početni datum"
            value={startDate}
            onChange={(date) => {
              if (date && !(dayjs.isDayjs(date))) {
                setStartDate(dayjs(date));
              } else {
                setStartDate(date);
              }
            }}
            maxDate={dayjs()}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DatePicker
            label="Krajnji datum"
            value={endDate}
            onChange={(date) => {
              if (date && !(dayjs.isDayjs(date))) {
                setEndDate(dayjs(date));
              } else {
                setEndDate(date);
              }
            }}
            maxDate={dayjs()}
            slotProps={{ textField: { size: 'small' } }}
          />
        </LocalizationProvider>
        <TextField
          select
          SelectProps={{ multiple: true, renderValue: (selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(selected as string[]).map((value) => (
                <Box key={value} sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', px: 1, borderRadius: 1, fontSize: 13 }}>{value}</Box>
              ))}
            </Box>
          ) }}
          label="Lambda grupe"
          value={selectedLambdaGroups}
          onChange={(e) => setSelectedLambdaGroups(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
          size="small"
          sx={{ minWidth: 160 }}
        >
          {lambdaGroups.map((g) => (
            <MenuItem key={g._id} value={g.name}>
              <Checkbox checked={selectedLambdaGroups.indexOf(g.name) > -1} />
              <ListItemText primary={g.name} />
            </MenuItem>
          ))}
        </TextField>
        <Autocomplete
          multiple
          options={productOptionsForFilter}
          value={selectedProductsFilter}
          onChange={(_, value) => setSelectedProductsFilter(value)}
          disableCloseOnSelect
          getOptionLabel={(option) => option}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox style={{ marginRight: 8 }} checked={selected} />
              {option}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Proizvodi"
              placeholder="Pretraži..."
              size="small"
            />
          )}
          slotProps={{
            paper: { sx: { minWidth: 320 } }
          }}
          ListboxProps={{
            style: { maxHeight: 6 * 48, overflow: 'auto' }
          }}
          sx={{ minWidth: 280 }}
          disabled={selectedLambdaGroups.length === 0 || productOptionsForFilter.length === 0}
        />
        <Autocomplete
          multiple
          options={thicknessOptions}
          value={selectedThicknesses}
          onChange={(_, value) => setSelectedThicknesses(value)}
          disableCloseOnSelect
          getOptionLabel={(option) => `${option} mm`}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox style={{ marginRight: 8 }} checked={selected} />
              {option} mm
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Debljine" placeholder="Pretraži..." size="small" />
          )}
          slotProps={{
            paper: { sx: { minWidth: 200 } }
          }}
          ListboxProps={{
            style: { maxHeight: 6 * 48, overflow: 'auto' }
          }}
          sx={{ minWidth: 160 }}
          disabled={selectedLambdaGroups.length === 0 || thicknessOptions.length === 0}
        />
        {/* MultiSelect za kolone, samo za masteradmina */}
        {(role === 'master_admin' || role === 'admin' || role === 'direktor') && (
          <Box>
            <Button variant="outlined" onClick={openPopover} sx={{ minWidth: 250 }}>
              Selektuj kolone za prikaz
            </Button>
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={closePopover}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { width: 450, maxHeight: 500, p: 2 } }}
            >
              <DndContext
                collisionDetection={closestCenter}
                onDragEnd={({ active, over }) => {
                  if (active.id !== over?.id) {
                    const oldIndex = columns.findIndex(c => c.key === active.id);
                    const newIndex = over ? columns.findIndex(c => c.key === over.id) : -1;
                    if (newIndex !== -1) {
                      setColumns(cols => {
                        const next = arrayMove(cols, oldIndex, newIndex).map((col, i) => ({ ...col, order: i }));
                        const payload = next.map((col: any, i: number) => ({ key: col.key, visible: col.visible, order: i }));
                        tableSettingsApi.saveColumnSettings(payload, false).catch(() => {});
                        return next;
                      });
                    }
                  }
                }}
              >
                <SortableContext
                  items={columns.map(col => col.key)}
                  strategy={verticalListSortingStrategy}
                >
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {columns.map((col, idx) => (
                      <SortableColumnItem
                        key={col.key}
                        col={col}
                        idx={idx}
                        id={col.key}
                        onToggleVisibility={handleToggleColumnVisibility}
                      />
                    ))}
                  </Box>
                </SortableContext>
              </DndContext>
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button size="small" variant="outlined" onClick={async () => { await saveColumns(false); closePopover(); }} disabled={columnsLoading}>Sačuvaj promene</Button>
                {role === 'master_admin' && (
                  <Button size="small" variant="contained" color="primary" onClick={async () => { await saveColumns(true); closePopover(); }} disabled={columnsLoading}>
                    Sačuvaj kao globalna
                  </Button>
                )}
                <Button size="small" variant="outlined" onClick={resetToGlobalSettings} disabled={columnsLoading}>Reset na globalna</Button>
              </Box>
            </Popover>
          </Box>
        )}
        {(role === 'admin' || role === 'master_admin' || role === 'direktor') && (
          <>
            <FormControlLabel
              control={<Checkbox checked={excludeRejected} onChange={e => setExcludeRejected(e.target.checked)} />}
              label="Isključi odbijene"
            />
            <FormControlLabel
              control={<Checkbox checked={excludeVerified} onChange={e => setExcludeVerified(e.target.checked)} />}
              label="Isključi verifikovane"
            />
          </>
        )}
        {hasActiveFilters && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<RestoreIcon />}
            onClick={handleResetFilters}
            sx={{ ml: isMobile ? 0 : 1 }}
          >
            Reset filtera
          </Button>
        )}
      </Box>



      <Paper sx={{ 
        p: isMobile ? 2 : 3, 
        mb: isMobile ? 2 : 3,
        '@media (max-width: 480px)': {
          p: 1,
          mb: 1.5,
        }
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography 
              variant="h6"
              sx={{
                '@media (max-width: 480px)': {
                  fontSize: '1rem',
                }
              }}
            >
            Eksport podataka
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: isMobile ? 1 : 2,
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            '@media (max-width: 480px)': {
              gap: 0.5,
            }
          }}>
            <Button
              variant="contained"
              startIcon={<ExcelIcon />}
              onClick={exportToExcel}
              disabled={measurements.length === 0}
              sx={{
                '@media (max-width: 480px)': {
                  fontSize: '0.75rem',
                  minHeight: 36,
                  px: 1,
                }
              }}
            >
              Exportuj u Excel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PdfIcon />}
              onClick={exportToPDF}
              disabled={measurements.length === 0}
              sx={{
                '@media (max-width: 480px)': {
                  fontSize: '0.75rem',
                  minHeight: 36,
                  px: 1,
                }
              }}
            >
              Exportuj u PDF
            </Button>
            {hasGraphableColumns && (
              <Button
                variant="outlined"
                startIcon={<ShowChartIcon />}
                onClick={() => setGraphDialogOpen(true)}
                disabled={sortedMeasurements.length === 0}
                sx={{
                  '@media (max-width: 480px)': {
                    fontSize: '0.75rem',
                    minHeight: 36,
                    px: 1,
                  }
                }}
              >
                Export Graph
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Kontrole za sortiranje – između Export podataka i tabele */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: isMobile ? 1 : 2, 
        mt: isMobile ? 2 : 3,
        mb: isMobile ? 1 : 2,
        flexDirection: isMobile ? 'column' : 'row',
        '@media (max-width: 480px)': {
          gap: 0.5,
          alignItems: 'stretch'
        }
      }}>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{
            '@media (max-width: 480px)': {
              fontSize: '0.875rem',
              textAlign: 'center',
              mb: 0.5
            }
          }}
        >
          Sortiraj po:
        </Typography>
        <TextField
          select
          label="Polje za sortiranje"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'datumProizvodnje' | 'datumTestiranja')}
          size="small"
          sx={{ 
            minWidth: isMobile ? '100%' : 160,
            '@media (max-width: 480px)': {
              fontSize: '0.875rem',
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                padding: '8px 12px'
              }
            }
          }}
        >
          <MenuItem value="datumProizvodnje">Datum proizvodnje</MenuItem>
          <MenuItem value="datumTestiranja">Datum testiranja</MenuItem>
        </TextField>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          startIcon={sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
          sx={{
            '@media (max-width: 480px)': {
              fontSize: '0.875rem',
              minHeight: 40,
              px: 2,
              flex: 1
            }
          }}
        >
          {sortOrder === 'asc' ? 'Rastuće' : 'Opadajuće'}
        </Button>
      </Box>

      <Paper sx={{ 
        width: '100%', 
        overflow: 'hidden',
        '@media (max-width: 480px)': {
          maxWidth: '100vw',
          overflow: 'auto',
        }
      }}>
        
        <TableContainer sx={{ 
          maxHeight: isMobile ? 400 : 600, 
          overflow: 'auto',
          width: '100%',
          '@media (max-width: 480px)': {
            maxHeight: 350,
            maxWidth: '100vw',
            overflow: 'auto',
          }
        }}>
                      <Table stickyHeader sx={{ 
              minWidth: isMobile ? 800 : 1200,
              width: '100%',
              '@media (max-width: 480px)': {
                minWidth: 600,
                maxWidth: '100vw',
                fontSize: '0.7rem',
              }
            }}>
            <TableHead>
              <TableRow>
                {columns
                  .filter(c => c.visible)
                  .sort((a, b) => a.order - b.order)
                  .map((col) => (
                    <TableCell 
                      key={col.key} 
                      sx={{ 
                        fontWeight: 'bold', 
                        bgcolor: 'primary.main', 
                        color: 'white',
                        whiteSpace: 'nowrap',
                        minWidth: isMobile ? 80 : 120,
                        maxWidth: isMobile ? 150 : 200,
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                        padding: isMobile ? '8px 4px' : '16px',
                        '@media (max-width: 480px)': {
                          minWidth: 50,
                          maxWidth: 100,
                          fontSize: '0.65rem',
                          padding: '4px 1px',
                        }
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedMeasurements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.filter(c => c.visible).length} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nema podataka za prikaz
                    </Typography>
                  </TableCell>
                </TableRow>
                              ) : (
                  paginatedMeasurements.map((m, idx) => (
                  <TableRow key={idx}>
                    {columns
                      .filter(c => c.visible)
                      .sort((a, b) => a.order - b.order)
                      .map((col) => {
                        const cellValue = getColumnValue(col, m, products, lambdaGroups);

                        // Specijalan prikaz za kolonu "napomena" – oko ikonica + popup
                        if (col.key === 'napomena') {
                          const hasNote = !!cellValue && cellValue.trim().length > 0;
                          return (
                            <TableCell
                              key={col.key}
                              align="center"
                              sx={{
                                whiteSpace: 'nowrap',
                                minWidth: isMobile ? 60 : 80,
                                maxWidth: isMobile ? 80 : 100,
                                fontSize: isMobile ? '0.75rem' : '0.875rem',
                                padding: isMobile ? '8px 4px' : '16px',
                              }}
                            >
                              {hasNote ? (
                                <Tooltip title="Prikaži napomenu" placement="top">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenNoteDialog(cellValue)}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          );
                        }

                        // Default prikaz za sve ostale kolone
                        return (
                          <TableCell 
                            key={col.key}
                            sx={{
                              whiteSpace: 'nowrap',
                              minWidth: isMobile ? 80 : 120,
                              maxWidth: isMobile ? 150 : 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              fontSize: isMobile ? '0.75rem' : '0.875rem',
                              padding: isMobile ? '8px 4px' : '16px',
                              '@media (max-width: 480px)': {
                                minWidth: 50,
                                maxWidth: 100,
                                fontSize: '0.65rem',
                                padding: '4px 1px',
                              }
                            }}
                          >
                            <Tooltip
                              title={
                                <Box sx={{ whiteSpace: 'pre-wrap' }}>
                                  {cellValue}
                                </Box>
                              }
                              placement="top"
                            >
                              <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {cellValue}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Dijalog za prikaz pune napomene */}
        <Dialog open={noteDialogOpen} onClose={handleCloseNoteDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Napomena</DialogTitle>
          <DialogContent dividers>
            {noteDialogText.split(/(?:\r?\n|\\n)/).map((line, idx) => (
              <Typography
                key={idx}
                variant="body2"
                sx={{ mb: 0.5 }}
              >
                {line}
              </Typography>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseNoteDialog} color="primary">
              Zatvori
            </Button>
          </DialogActions>
        </Dialog>

        {/* Export Graph – linijski graf za izabrani period i uključene kolone */}
        <Dialog open={graphDialogOpen} onClose={() => setGraphDialogOpen(false)} fullScreen>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Graf – vremenski prikaz za izabrani period</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<PdfIcon />}
                onClick={async () => {
                  if (!graphChartData.length) return;
                  const container = chartContainerRef.current;
                  if (!container) {
                    alert('Greška pri eksportu grafa u PDF.');
                    return;
                  }
                  try {
                    const canvas = await html2canvas(container, {
                      useCORS: true,
                      scale: 2,
                      backgroundColor: '#ffffff',
                      logging: false,
                    });
                    const imgData = canvas.toDataURL('image/png');
                    const doc = new jsPDF('landscape');
                    const pageWidth = doc.internal.pageSize.getWidth();
                    const pageHeight = doc.internal.pageSize.getHeight();
                    const padding = 10;

                    doc.setFillColor(41, 128, 185);
                    doc.rect(0, 0, pageWidth, 45, 'F');
                    try {
                      doc.addImage(logoImage, 'PNG', 15, 8, 45, 18);
                    } catch (e) {}
                    doc.setTextColor(255, 255, 255);
                    doc.setFontSize(16);
                    doc.setFont('helvetica', 'bold');
                    doc.text('Izveštaj – Završna tabela', 70, 22);
                    doc.setFontSize(10);
                    doc.setFont('helvetica', 'normal');
                    doc.text('Tehnicka merenja proizvoda', 70, 32);

                    const userFromStorage = JSON.parse(localStorage.getItem('user') || '{}');
                    let dateRangeText = '';
                    if (startDate && endDate) {
                      dateRangeText = `${startDate.format('DD.MM.YYYY')} - ${endDate.format('DD.MM.YYYY')}`;
                    } else if (startDate) {
                      dateRangeText = `${startDate.format('DD.MM.YYYY')}`;
                    } else if (endDate) {
                      dateRangeText = `${endDate.format('DD.MM.YYYY')}`;
                    } else {
                      dateRangeText = format(new Date(), 'dd.MM.yyyy');
                    }
                    doc.setTextColor(0, 0, 0);
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'bold');
                    doc.text(`Period: ${dateRangeText}`, 15, 55);
                    doc.setFont('helvetica', 'normal');
                    doc.text(`Ukupno merenja: ${graphChartData.length}`, 15, 60);
                    doc.text(`Izveštaj predao: ${userFromStorage.username || ''}`, 15, 65);
                    doc.setFont('helvetica', 'bold');
                    doc.text(`Datum izveštaja: ${format(new Date(), 'dd.MM.yyyy HH:mm')}`, 15, 70);
                    doc.setFont('helvetica', 'normal');

                    const imgWidth = pageWidth - 2 * padding;
                    const startY = 80;
                    const maxImgHeight = pageHeight - startY - 20;
                    const imgHeight = Math.min((canvas.height / canvas.width) * imgWidth, maxImgHeight);
                    doc.addImage(imgData, 'PNG', padding, startY, imgWidth, imgHeight);

                    doc.setFontSize(9);
                    doc.setTextColor(0, 0, 0);
                    doc.text('Legenda boja: Zeleno = u opsegu | Žuto = ispod min | Crveno = iznad max', padding, startY + imgHeight + 8);

                    const sortedGraphData = [...graphChartData].sort((a: any, b: any) => (a.dateSort as number) - (b.dateSort as number));
                    const metricMinMax = visibleGraphableColumns.map((col: any) => {
                      const vals = sortedGraphData.map((p: any) => p[col.key]).filter((v: any) => v != null && !isNaN(Number(v))).map(Number);
                      return { key: col.key, min: vals.length ? Math.min(...vals) : 0, max: vals.length ? Math.max(...vals) : 100 };
                    });
                    const tableHeaders = ['Naziv proizvoda', 'Datum', ...visibleGraphableColumns.map((c: any) => GRAPHABLE_FIELD_MAP[c.key]?.label ?? c.label)];
                    const tableBody = sortedGraphData.map((p: any) => {
                      type CellInput = string | { content: string; styles?: { fillColor: [number, number, number] } };
                      const row: CellInput[] = [
                        p.productType ?? '',
                        p.date ?? '',
                      ];
                      visibleGraphableColumns.forEach((col: any) => {
                        const val = p[col.key];
                        const num = val != null && !isNaN(Number(val)) ? Number(val) : null;
                        const mm = metricMinMax.find((m: any) => m.key === col.key);
                        let fillColor: [number, number, number] = [255, 255, 255];
                        if (num !== null && mm) {
                          if (num >= mm.max) fillColor = [239, 154, 154];
                          else if (num <= mm.min) fillColor = [255, 249, 196];
                          else fillColor = [200, 230, 201];
                        }
                        row.push({ content: num !== null ? num.toFixed(2) : '–', styles: { fillColor } });
                      });
                      return row;
                    });

                    doc.addPage('landscape');
                    autoTable(doc, {
                      head: [tableHeaders],
                      body: tableBody,
                      startY: padding,
                      margin: { left: padding, right: padding },
                      theme: 'grid',
                      headStyles: { fillColor: [158, 158, 158], fontSize: 8 },
                      bodyStyles: { fontSize: 7 },
                      columnStyles: { 0: { cellWidth: 45 }, 1: { cellWidth: 22, fontStyle: 'bold' } },
                    });

                    doc.save(`graf-zavrsna-tabela-${format(new Date(), 'dd-MM-yyyy-HHmm')}.pdf`);
                  } catch (e) {
                    alert('Greška pri eksportu grafa u PDF.');
                  }
                }}
              >
                Export Graph PDF
              </Button>
              <IconButton aria-label="Zatvori" onClick={() => setGraphDialogOpen(false)} size="large">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ bgcolor: 'background.default' }}>
            {graphChartData.length === 0 ? (
              <Typography color="text.secondary">Nema podataka za prikaz u izabranom periodu.</Typography>
            ) : (() => {
              const sortedGraphData = [...graphChartData].sort((a: any, b: any) => (a.dateSort as number) - (b.dateSort as number));
              const uniqueDates = Array.from(new Set(sortedGraphData.map((p: any) => p.date)));
              const allNums = sortedGraphData.flatMap((p: any) =>
                visibleGraphableColumns.map((c: any) => p[c.key]).filter((v: any) => v != null && !isNaN(Number(v)))
              ).map(Number);
              const yMin = allNums.length ? Math.min(...allNums) : 0;
              const yMax = allNums.length ? Math.max(...allNums) : 100;
              const metricMinMax = visibleGraphableColumns.map((col: any) => {
                const vals = sortedGraphData.map((p: any) => p[col.key]).filter((v: any) => v != null && !isNaN(Number(v))).map(Number);
                return { key: col.key, min: vals.length ? Math.min(...vals) : 0, max: vals.length ? Math.max(...vals) : 100 };
              });
              return (
              <Box sx={{ width: '100%', background: '#fff' }}>
                <Box ref={chartContainerRef}>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart
                    data={sortedGraphData}
                    margin={{ top: 8, right: 24, left: 8, bottom: 32 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      ticks={uniqueDates}
                      tick={{ fontSize: 10, fontWeight: 'bold' }}
                      tickFormatter={(value: string) => (value && value.length >= 5 ? value.slice(0, 5) : value) || ''}
                      height={40}
                    />
                    <YAxis
                      domain={[yMin, yMax]}
                      ticks={yMin === yMax ? [yMin] : [yMin, (yMin + yMax) / 2, yMax]}
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v: number) => Number(v).toFixed(2)}
                    />
                    <RechartsTooltip
                      formatter={(value: number, name: string) => [value != null && !isNaN(Number(value)) ? Number(value).toFixed(2) : '–', GRAPHABLE_FIELD_MAP[name]?.label ?? name]}
                      labelFormatter={(label, payload) => payload?.[0]?.payload?.productType ? `${label} — ${(payload[0].payload as any).productType}` : label}
                    />
                    <Legend />
                    {visibleGraphableColumns.map((col: any, idx: number) => (
                      <Line
                        key={col.key}
                        type="monotone"
                        dataKey={col.key}
                        name={GRAPHABLE_FIELD_MAP[col.key]?.label ?? col.label}
                        stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
                </Box>
                <TableContainer sx={{ maxHeight: 280, overflow: 'auto', mt: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Naziv proizvoda</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Datum</TableCell>
                        {visibleGraphableColumns.map((col: any) => (
                          <TableCell key={col.key} sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>
                            {GRAPHABLE_FIELD_MAP[col.key]?.label ?? col.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedGraphData.map((p: any, idx: number) => (
                        <TableRow key={idx}>
                          <TableCell>{p.productType}</TableCell>
                          <TableCell>{p.date}</TableCell>
                          {visibleGraphableColumns.map((col: any) => {
                            const val = p[col.key];
                            const num = val != null && !isNaN(Number(val)) ? Number(val) : null;
                            const mm = metricMinMax.find((m: any) => m.key === col.key);
                            let bg = 'transparent';
                            if (num !== null && mm) {
                              if (num >= mm.max) bg = 'rgba(211, 47, 47, 0.3)';
                              else if (num <= mm.min) bg = 'rgba(255, 235, 59, 0.5)';
                              else bg = 'rgba(76, 175, 80, 0.25)';
                            }
                            return (
                              <TableCell key={col.key} sx={{ bgcolor: bg, fontWeight: 500 }}>
                                {num !== null ? num.toFixed(2) : '–'}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              );
            })()}
          </DialogContent>
        </Dialog>

        {sortedMeasurements.length > 10 && (
          <TablePagination
            component="div"
            count={sortedMeasurements.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={isMobile ? [10, 25] : [10, 25, 50, 100]}
            labelRowsPerPage="Prikaz po strani"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} od ${count}`}
            sx={{
              '@media (max-width: 480px)': {
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.75rem',
                },
                '& .MuiTablePagination-select': {
                  fontSize: '0.75rem',
                }
              }
            }}
          />
        )}
        {sortedMeasurements.length <= 10 && sortedMeasurements.length > 0 && (
          <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">
              Prikazano {sortedMeasurements.length} od {sortedMeasurements.length} merenja
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default FinalReport; 